/**
 * Phase 107 (Plan 107-08 / D-12, T-107-MONEYDRIFT) — the price summary card.
 *
 * Ports the recovered `CoverageTeamSidebar.tsx` (git show bec322bc)'s
 * Summary card markup, promoted to its own file and to the **first** card in
 * the sidebar (107-UI-SPEC.md — in this phase the price is the product,
 * unlike the deleted page where teams led). The amber
 * `ExclamationTriangleIcon` warning box is the same pattern the recovered
 * sidebar used for its package-allowance warning, reused here for the D-09
 * invalid-band warning.
 *
 * This component performs **no** money arithmetic. Every number it renders
 * arrives already computed by `priceMath.ts` (Plan 107-07) — the recovered
 * component's client-side float aggregation is not carried forward. If this
 * file and `priceMath.ts` ever disagree about what the total looks like,
 * that is a bug in this file, not a second implementation of the formula.
 */
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import type { ClientBandBreakdown } from './priceMath';

const CURRENCY_SYMBOL: Record<string, string> = {
  EUR: '€',
  USD: '$',
  CZK: 'Kč',
};

function symbolFor(currency: string): string {
  return CURRENCY_SYMBOL[currency] ?? currency;
}

export interface InvalidBand {
  id: number;
  name: string;
}

export interface PriceSummaryCardProps {
  monthlyTotal: string;
  currency: string;
  breakdown: ClientBandBreakdown[];
  hasPaintedCells: boolean;
  invalidBands: InvalidBand[];
}

function invalidBandWarningCopy(invalidBands: InvalidBand[]): string {
  if (invalidBands.length === 1) {
    return `"${invalidBands[0]!.name}" needs an hourly rate — set it in Price bands.`;
  }
  const list = invalidBands.map((b) => `"${b.name}"`).join(', ');
  return `${invalidBands.length} bands need an hourly rate: ${list}. Set them in Price bands.`;
}

export function PriceSummaryCard({
  monthlyTotal,
  currency,
  breakdown,
  hasPaintedCells,
  invalidBands,
}: PriceSummaryCardProps) {
  const symbol = symbolFor(currency);

  return (
    <div className="rounded border border-ms-border bg-white p-3">
      <p className="text-[10.5px] font-semibold uppercase tracking-wider text-ms-text-muted">
        Monthly price
      </p>

      {!hasPaintedCells ? (
        <p className="mt-2 text-[13px] text-ms-text-secondary">Nothing painted yet.</p>
      ) : (
        <>
          <p className="text-2xl font-bold tabular-nums text-ms-text-strong">
            {symbol}
            {monthlyTotal}
          </p>

          {invalidBands.length > 0 && (
            <div
              role="alert"
              className="mt-2 rounded border border-amber-200 bg-amber-50 p-2 flex items-start gap-2"
            >
              <ExclamationTriangleIcon
                className="h-3.5 w-3.5 text-amber-600 shrink-0"
                aria-hidden="true"
              />
              <span className="text-[12px] text-amber-800">
                {invalidBandWarningCopy(invalidBands)}
              </span>
            </div>
          )}

          <ul className="mt-3 space-y-1">
            {breakdown.map((row) => (
              <li key={row.bandId} className="flex items-center justify-between text-[12px]">
                <span className="flex items-center gap-2">
                  <span
                    className="h-3 w-3 rounded"
                    style={{ backgroundColor: row.color }}
                    aria-hidden="true"
                  />
                  <span className="text-ms-text-strong">{row.name}</span>
                </span>
                <span className="font-mono tabular-nums text-ms-text-secondary">
                  {row.hoursPerWeek}h ·{' '}
                  {row.hasRate ? (
                    `${symbol}${row.monthlyAmount}`
                  ) : (
                    <span className="text-rose-600">— {symbol}</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

export default PriceSummaryCard;
